/**
 * 계측(Measure) 타입 상수 정의
 * Feature ICT-31: 24개 계측 항목 스키마
 *
 * DOCX 렌더링 및 GraphQL API에서 사용되는 계측 타입 상수
 */

/**
 * 측정 카테고리 (7개)
 */
export const MeasureCategory = {
  ELECTRICAL: "electrical", // 전기
  COMMUNICATION: "communication", // 통신환경
  RADIO_WAVE: "radioWave", // 전파환경
  WIRELESS: "wireless", // 무선신호
  FIELD_STRENGTH: "fieldStrength", // 전계강도
  RF_CARD: "rfCard", // RF카드
  MISC: "misc", // 기타
} as const;

export type MeasureCategoryType = (typeof MeasureCategory)[keyof typeof MeasureCategory];

/**
 * 측정 타입 키 (24개)
 * DOCX 테이블 구조 기반 측정 항목 식별자
 */
export const MeasureTypeKey = {
  // 전기 (7개)
  AC_VOLTAGE: "acVoltage", // AC 입력 전압
  DC_VOLTAGE: "dcVoltage", // DC 입력 전압
  GROUND_RESISTANCE: "groundResistance", // 접지저항
  TERMINATION_RESISTANCE: "terminationResistance", // 종단 저항
  CHARGING_VOLTAGE_CURRENT: "chargingVoltageCurrent", // 충전전압/전류
  BATTERY_VOLTAGE: "batteryVoltage", // 배터리 전압
  CLOCK_LINE_VOLTAGE: "clockLineVoltage", // 전자시계 회선별 전압

  // 통신환경 (5개)
  LINK_SPEED: "linkSpeed", // 링크 동작 속도
  OPTICAL_LEVEL: "opticalLevel", // 광 송수신 레벨
  TX_OUTPUT: "txOutput", // 송신 출력
  OUTPUT_TERMINAL_LEVEL: "outputTerminalLevel", // 출력검사단자 레벨값
  IO_SIGNAL_LEVEL: "ioSignalLevel", // 기기 입출력 신호레벨

  // 전파환경 (3개)
  RSRP_TERRESTRIAL: "rsrpTerrestrial", // RSRP 지상파
  RSRP_FM_RADIO: "rsrpFmRadio", // RSRP FM라디오
  RSRP_SATELLITE: "rsrpSatellite", // RSRP 위성방송

  // 무선신호 (2개)
  EMERGENCY_BELL_SENSITIVITY: "emergencyBellSensitivity", // 비상벨 수신감도
  REMOTE_METERING_SENSITIVITY: "remoteMeteringSensitivity", // 원격검침 수신감도

  // 전계강도 (2개)
  COAXIAL_FIELD_STRENGTH: "coaxialFieldStrength", // 동축케이블 전계강도
  WIRELESS_FIELD_STRENGTH: "wirelessFieldStrength", // 무선신호 전계강도

  // RF카드 (2개)
  RF_MAGNETIC_FIELD: "rfMagneticField", // 자기장 세기
  RF_RECOGNITION_DISTANCE: "rfRecognitionDistance", // 인식거리

  // 기타 (3개)
  TEMPERATURE_HUMIDITY: "temperatureHumidity", // 온습도
  BATTERY_SPECIFIC_GRAVITY: "batterySpecificGravity", // 축전지 비중
  MANHOLE_TOXIC_GAS: "manholeToxicGas", // 맨홀 유해가스
} as const;

export type MeasureTypeKeyType = (typeof MeasureTypeKey)[keyof typeof MeasureTypeKey];

/**
 * 측정 결과
 * DOCX 테이블의 측정결과 열 값
 */
export const MeasureResult = {
  GOOD: "GOOD", // 양호
  BAD: "BAD", // 불량
  NA: "NA", // 해당 없음
} as const;

export type MeasureResultType = (typeof MeasureResult)[keyof typeof MeasureResult];

/**
 * 카테고리별 측정 타입 매핑
 * 각 카테고리에 속하는 측정 타입 목록
 */
export const MeasureTypesByCategory = {
  [MeasureCategory.ELECTRICAL]: [
    MeasureTypeKey.AC_VOLTAGE,
    MeasureTypeKey.DC_VOLTAGE,
    MeasureTypeKey.GROUND_RESISTANCE,
    MeasureTypeKey.TERMINATION_RESISTANCE,
    MeasureTypeKey.CHARGING_VOLTAGE_CURRENT,
    MeasureTypeKey.BATTERY_VOLTAGE,
    MeasureTypeKey.CLOCK_LINE_VOLTAGE,
  ],
  [MeasureCategory.COMMUNICATION]: [
    MeasureTypeKey.LINK_SPEED,
    MeasureTypeKey.OPTICAL_LEVEL,
    MeasureTypeKey.TX_OUTPUT,
    MeasureTypeKey.OUTPUT_TERMINAL_LEVEL,
    MeasureTypeKey.IO_SIGNAL_LEVEL,
  ],
  [MeasureCategory.RADIO_WAVE]: [
    MeasureTypeKey.RSRP_TERRESTRIAL,
    MeasureTypeKey.RSRP_FM_RADIO,
    MeasureTypeKey.RSRP_SATELLITE,
  ],
  [MeasureCategory.WIRELESS]: [
    MeasureTypeKey.EMERGENCY_BELL_SENSITIVITY,
    MeasureTypeKey.REMOTE_METERING_SENSITIVITY,
  ],
  [MeasureCategory.FIELD_STRENGTH]: [
    MeasureTypeKey.COAXIAL_FIELD_STRENGTH,
    MeasureTypeKey.WIRELESS_FIELD_STRENGTH,
  ],
  [MeasureCategory.RF_CARD]: [
    MeasureTypeKey.RF_MAGNETIC_FIELD,
    MeasureTypeKey.RF_RECOGNITION_DISTANCE,
  ],
  [MeasureCategory.MISC]: [
    MeasureTypeKey.TEMPERATURE_HUMIDITY,
    MeasureTypeKey.BATTERY_SPECIFIC_GRAVITY,
    MeasureTypeKey.MANHOLE_TOXIC_GAS,
  ],
} as const;

/**
 * 측정 타입별 단위 정보
 */
export const MeasureUnits = {
  [MeasureTypeKey.AC_VOLTAGE]: "V",
  [MeasureTypeKey.DC_VOLTAGE]: "V",
  [MeasureTypeKey.GROUND_RESISTANCE]: "Ω",
  [MeasureTypeKey.TERMINATION_RESISTANCE]: "Ω",
  [MeasureTypeKey.CHARGING_VOLTAGE_CURRENT]: "V, A",
  [MeasureTypeKey.BATTERY_VOLTAGE]: "V, A",
  [MeasureTypeKey.CLOCK_LINE_VOLTAGE]: "V",
  [MeasureTypeKey.LINK_SPEED]: "Mbps/Gbps",
  [MeasureTypeKey.OPTICAL_LEVEL]: "dBm",
  [MeasureTypeKey.TX_OUTPUT]: "mW",
  [MeasureTypeKey.OUTPUT_TERMINAL_LEVEL]: "dBuV",
  [MeasureTypeKey.IO_SIGNAL_LEVEL]: "dBuV",
  [MeasureTypeKey.RSRP_TERRESTRIAL]: "dB, dBm",
  [MeasureTypeKey.RSRP_FM_RADIO]: "dB, dBm",
  [MeasureTypeKey.RSRP_SATELLITE]: "dB, dBm",
  [MeasureTypeKey.EMERGENCY_BELL_SENSITIVITY]: "dBm",
  [MeasureTypeKey.REMOTE_METERING_SENSITIVITY]: "dBm",
  [MeasureTypeKey.COAXIAL_FIELD_STRENGTH]: "μV/m",
  [MeasureTypeKey.WIRELESS_FIELD_STRENGTH]: "μV/m",
  [MeasureTypeKey.RF_MAGNETIC_FIELD]: "A/m",
  [MeasureTypeKey.RF_RECOGNITION_DISTANCE]: "m",
  [MeasureTypeKey.TEMPERATURE_HUMIDITY]: "℃, %",
  [MeasureTypeKey.BATTERY_SPECIFIC_GRAVITY]: "S.G",
  [MeasureTypeKey.MANHOLE_TOXIC_GAS]: "%, ppm",
} as const;

/**
 * 측정 타입별 한글 이름
 */
export const MeasureTypeNames = {
  [MeasureTypeKey.AC_VOLTAGE]: "AC 입력 전압",
  [MeasureTypeKey.DC_VOLTAGE]: "DC 입력 전압",
  [MeasureTypeKey.GROUND_RESISTANCE]: "접지저항",
  [MeasureTypeKey.TERMINATION_RESISTANCE]: "종단 저항",
  [MeasureTypeKey.CHARGING_VOLTAGE_CURRENT]: "충전전압/전류",
  [MeasureTypeKey.BATTERY_VOLTAGE]: "배터리 전압",
  [MeasureTypeKey.CLOCK_LINE_VOLTAGE]: "전자시계 회선별 전압",
  [MeasureTypeKey.LINK_SPEED]: "링크 동작 속도",
  [MeasureTypeKey.OPTICAL_LEVEL]: "광 송수신 레벨",
  [MeasureTypeKey.TX_OUTPUT]: "송신 출력",
  [MeasureTypeKey.OUTPUT_TERMINAL_LEVEL]: "출력검사단자 레벨값",
  [MeasureTypeKey.IO_SIGNAL_LEVEL]: "기기 입출력 신호레벨",
  [MeasureTypeKey.RSRP_TERRESTRIAL]: "RSRP 지상파",
  [MeasureTypeKey.RSRP_FM_RADIO]: "RSRP FM라디오",
  [MeasureTypeKey.RSRP_SATELLITE]: "RSRP 위성방송",
  [MeasureTypeKey.EMERGENCY_BELL_SENSITIVITY]: "비상벨 수신감도",
  [MeasureTypeKey.REMOTE_METERING_SENSITIVITY]: "원격검침 수신감도",
  [MeasureTypeKey.COAXIAL_FIELD_STRENGTH]: "동축케이블 전계강도",
  [MeasureTypeKey.WIRELESS_FIELD_STRENGTH]: "무선신호 전계강도",
  [MeasureTypeKey.RF_MAGNETIC_FIELD]: "자기장 세기",
  [MeasureTypeKey.RF_RECOGNITION_DISTANCE]: "인식거리",
  [MeasureTypeKey.TEMPERATURE_HUMIDITY]: "온습도",
  [MeasureTypeKey.BATTERY_SPECIFIC_GRAVITY]: "축전지 비중",
  [MeasureTypeKey.MANHOLE_TOXIC_GAS]: "맨홀 유해가스",
} as const;

/**
 * 카테고리별 한글 이름
 * UI 표시 및 리포트 렌더링에서 사용
 */
export const MeasureCategoryNames = {
  [MeasureCategory.ELECTRICAL]: "전기",
  [MeasureCategory.COMMUNICATION]: "통신환경",
  [MeasureCategory.RADIO_WAVE]: "전파환경",
  [MeasureCategory.WIRELESS]: "무선신호",
  [MeasureCategory.FIELD_STRENGTH]: "전계강도",
  [MeasureCategory.RF_CARD]: "RF카드",
  [MeasureCategory.MISC]: "기타",
} as const;

export type MeasureCategoryNameType =
  (typeof MeasureCategoryNames)[keyof typeof MeasureCategoryNames];
